/**
 * `SoundProvider` — les sons du jeu (SPEC-UI §7.6).
 *
 * **Aucune génération** : les fichiers viennent de la banque locale de Mathieu (PLAN §7,
 * CLAUDE.md), choisis dans l'esprit de la DA ⟦A-6⟧ — du papier qu'on tourne, du carton qu'on
 * froisse, du scotch qu'on déchire. Leur provenance est dans `public/audio/PROVENANCE.md`.
 *
 * Trois décisions qui comptent :
 *
 * 1. **Web Audio, pas `new Audio()`.** Un `<audio>` a une latence de plusieurs dizaines de
 *    millisecondes et elle varie — inacceptable pour un jeu de rythme dont la fenêtre fait
 *    118 ms au palier max. On décode chaque son **une fois** en `AudioBuffer` et on rejoue le
 *    buffer : la latence tombe au minimum que la machine sait faire.
 * 2. **Silence par défaut jusqu'au premier geste.** Les navigateurs bloquent l'audio tant que
 *    l'utilisateur n'a rien touché : le contexte est créé et repris au premier clic ou à la
 *    première touche, jamais avant. Aucun avertissement dans la console, aucun son manqué au
 *    hasard.
 * 3. **Un id absent du manifeste est un no-op silencieux**, pas une erreur (§7.6 : « en M2-M3
 *    le manifeste peut mapper vers `null` »). On peut donc retirer un fichier sans rien casser.
 */

import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from "react";

import { LecteurMusique, type ManifesteMusique } from "../audio/musique";
import { useUIStore } from "../stores/uiStore";

/** La liste FERMÉE des événements sonores V1 (SPEC-UI §7.6). */
export type SfxId =
  | "ui_click"
  | "dialog_open"
  | "dialog_close"
  | "publish_whoosh"
  | "views_tick"
  | "palier_jingle"
  | "plaque_jingle"
  | "event_notif"
  | "good_buzz_sting"
  | "bad_buzz_sting"
  | "cut_hit"
  | "cut_perfect"
  | "cut_miss"
  | "take_hit"
  | "take_miss"
  | "cursor_stop"
  | "clip_place"
  | "chrono_last5"
  // ⟦A-76⟧ Choisis et déclarés ; ⟦A-83⟧ appelés — l'Arbre pour les deux premiers,
  // `audio/SonsDuJeu.tsx` (sur l'action réussie) pour les trois autres :
  | "skill_unlock"
  | "respec"
  | "cash_in"
  | "cash_out"
  | "sleep_transition"
  // ⟦C22 · A-151⟧ Les trois SFX de l'intro d'ouverture de chaîne (`screens/Intro/Intro.tsx`) :
  // un sting court à l'allumage cathodique, un « clac » quand le compteur d'abonnés touche
  // zéro, un tic discret par lettre tapée du nom de chaîne (coupé sous mouvement réduit, les
  // trois n'ont rien à ponctuer dans la version condensée).
  | "intro_allumage"
  | "intro_compteur_clac"
  | "intro_frappe_tic"
  // ⟦C27 · A-153⟧ Le glissement de l'interface après l'accueil (`screens/Accueil/Accueil.tsx`) :
  // un des 5 sons proposés par C28 (`docs/chantiers/C28-constat.md` §3), pas encore branché
  // ailleurs. Coupé sous mouvement réduit : le glissement lui-même ne s'y produit plus.
  | "fenetre_glisse"
  // ⟦C61 · A-178⟧ Les applaudissements de la fête, au dernier tableau de la cérémonie
  // (`screens/Fins/Ceremonie.tsx`) : une foule de la banque locale « 1,001 Sound FX », rien de généré.
  | "ceremonie_applaudissements";

interface Manifeste {
  sfx: Partial<Record<SfxId, string | null>>;
  music: ManifesteMusique;
}

type JouerSfx = (id: SfxId, options?: { volume?: number }) => void;

const ContexteSon = createContext<JouerSfx>(() => {});

/**
 * Le lecteur de musique (⟦A-71⟧, SPEC-UI §7.6 `useMusic()`). `null` hors du `SoundProvider` —
 * ce qui n'arrive qu'en test isolé, et rend alors tout appel inoffensif.
 */
const ContexteMusique = createContext<LecteurMusique | null>(null);

/** `useSfx()` — rend une fonction `jouer(id)`. Silencieuse si le son n'existe pas. */
export function useSfx(): JouerSfx {
  return useContext(ContexteSon);
}

/**
 * `useMusique()` — le lecteur de musique, ou `null`.
 *
 * ⚠️ **Un écran ne doit JAMAIS dépendre de ce qu'il rend.** `null` (hors provider), un id absent
 * du manifeste, un fichier illisible, le son coupé : ce sont quatre chemins normaux vers le
 * silence, et l'écran doit se dérouler pareil dans les quatre cas. La règle est écrite en
 * détail dans `audio/beatmap.ts` — si retirer la musique change une réussite, c'est le
 * branchement qui est faux.
 */
export function useMusique(): LecteurMusique | null {
  return useContext(ContexteMusique);
}

export function SoundProvider({ children }: { children: ReactNode }) {
  const mute = useUIStore((s) => s.settings.mute);
  const volume = useUIStore((s) => s.settings.volume);

  const contexte = useRef<AudioContext | null>(null);
  const gain = useRef<GainNode | null>(null);
  const buffers = useRef(new Map<SfxId, AudioBuffer>());
  const manifeste = useRef<Manifeste | null>(null);
  const reglages = useRef({ mute, volume });
  reglages.current = { mute, volume };

  // Le manifeste se charge tout de suite ; les fichiers, eux, attendent le premier geste.
  useEffect(() => {
    let vivant = true;
    void fetch("audio/manifest.json")
      .then((r) => (r.ok ? (r.json() as Promise<Manifeste>) : null))
      .then((m) => {
        if (vivant && m) manifeste.current = m;
      })
      .catch(() => {
        // Pas de manifeste : le jeu est muet, et c'est tout. Ce n'est pas une panne.
      });
    return () => {
      vivant = false;
    };
  }, []);

  /** Crée le contexte audio au premier geste de l'utilisateur (politique d'autoplay). */
  const reveiller = useCallback(() => {
    if (contexte.current) {
      if (contexte.current.state === "suspended") void contexte.current.resume();
      return contexte.current;
    }
    try {
      const ctx = new AudioContext();
      const sortie = ctx.createGain();
      sortie.connect(ctx.destination);
      contexte.current = ctx;
      gain.current = sortie;
      return ctx;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const auGeste = () => reveiller();
    window.addEventListener("pointerdown", auGeste, { passive: true });
    window.addEventListener("keydown", auGeste);
    return () => {
      window.removeEventListener("pointerdown", auGeste);
      window.removeEventListener("keydown", auGeste);
    };
  }, [reveiller]);

  const jouer = useCallback<JouerSfx>(
    (id, options) => {
      const { mute: muet, volume: vol } = reglages.current;
      if (muet) return;
      const chemin = manifeste.current?.sfx?.[id];
      if (!chemin) return; // id absent ou mappé à null : no-op silencieux, comme le veut §7.6
      const ctx = reveiller();
      if (!ctx || !gain.current) return;

      const lancer = (buffer: AudioBuffer) => {
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        const local = ctx.createGain();
        local.gain.value = Math.max(0, Math.min(1, vol * (options?.volume ?? 1)));
        source.connect(local).connect(gain.current!);
        source.start();
      };

      const dejaLa = buffers.current.get(id);
      if (dejaLa) {
        lancer(dejaLa);
        return;
      }
      void fetch(chemin)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error("absent"))))
        .then((données) => ctx.decodeAudioData(données))
        .then((buffer) => {
          buffers.current.set(id, buffer);
          lancer(buffer);
        })
        .catch(() => {
          // Fichier manquant ou illisible : on le neutralise pour ne pas réessayer à chaque
          // frappe, et le jeu continue en silence sur cet id-là.
          if (manifeste.current?.sfx) manifeste.current.sfx[id] = null;
        });
    },
    [reveiller],
  );

  /**
   * ⟦A-71⟧ Le lecteur de musique, monté **une seule fois** et branché sur le contexte audio
   * ci-dessus. Il ne crée rien tant que personne ne lui demande un morceau : un joueur qui ne
   * verra jamais la cérémonie ne télécharge jamais sa fanfare.
   */
  const musique = useRef<LecteurMusique | null>(null);
  if (musique.current === null) {
    musique.current = new LecteurMusique(
      () => reveiller(),
      () => gain.current,
      () => reglages.current,
      () => manifeste.current?.music ?? null,
    );
  }

  // Couper le son doit couper la musique EN COURS, pas seulement la suivante — sans quoi le
  // réglage a l'air cassé pendant la minute et demie d'une cérémonie.
  useEffect(() => {
    musique.current?.majReglages();
  }, [mute, volume]);

  // Un morceau ne doit pas survivre à la page qui l'a lancé.
  useEffect(() => () => musique.current?.arreter(), []);

  return (
    <ContexteSon.Provider value={jouer}>
      <ContexteMusique.Provider value={musique.current}>{children}</ContexteMusique.Provider>
    </ContexteSon.Provider>
  );
}
